'use client';

import { useChat } from '@ai-sdk/react';
import { DefaultChatTransport } from 'ai';
import type { ChatStatus, UIMessage } from 'ai';
import { createContext, ReactNode, useContext, useState } from 'react';
import { baseURL } from '../../../../client';
import type { PromptInputMessage } from '../components/elements/prompt-input';
import { authenticatedFetch } from '../utils/chat-fetch';

interface FullscreenWidget {
  toolName: string;
  result: any;
  args: any;
  title: string;
}

interface ChatContextValue {
  messages: UIMessage[];
  status: ChatStatus;
  regenerate: () => void;
  handleSubmit: (message: PromptInputMessage) => void;
  fullscreenWidget: FullscreenWidget | null;
  setFullscreenWidget: (widget: FullscreenWidget | null) => void;
}

const ChatContext = createContext<ChatContextValue | undefined>(undefined);

export function ChatProvider({ children }: { children: ReactNode }) {
  const { messages, sendMessage, status, regenerate } = useChat({
    experimental_throttle: 100,
    transport: new DefaultChatTransport({
      api: `${baseURL}/chats/stream-ai`,
      fetch: authenticatedFetch,
    }),
  });

  const [fullscreenWidget, setFullscreenWidget] = useState<FullscreenWidget | null>(null);

  const handleSubmit = (message: PromptInputMessage) => {
    const hasText = Boolean(message.text);
    const hasAttachments = Boolean(message.files?.length);
    if (!(hasText || hasAttachments)) {
      return;
    }
    sendMessage({
      text: message.text || 'Sent with attachments',
      files: message.files,
    });
  };

  return (
    <ChatContext.Provider
      value={{
        messages,
        status,
        regenerate,
        handleSubmit,
        fullscreenWidget,
        setFullscreenWidget,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
}

export function useChatContext() {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChatContext must be used within ChatProvider');
  }
  return context;
}
